import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Instagram } from 'lucide-react';
import { blogPosts, faqs, companyInfo } from '../mock';
import { useApp } from '../context/AppContext';
import { SectionHeading } from './LatestProjects';
import { useTranslated } from '../hooks/useTranslated';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import { Button } from './ui/button';

export const BlogSection = () => {
  const { t } = useApp();
  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
      <SectionHeading title={t('news')} linkLabel={t('view_all_posts')} to="/contact" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {blogPosts.map((b) => (
          <article key={b.id} className="group bg-white rounded-xl overflow-hidden border border-gobra-sand card-lift">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={b.image} alt={b.title} loading="lazy" className="h-full w-full object-cover img-zoom" />
            </div>
            <div className="p-5">
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>{b.date}</span>
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {b.read} {t('min_read')}</span>
              </div>
              <h3 className="font-title text-lg font-semibold text-gobra-slate mt-2 leading-snug group-hover:text-gobra-green transition-colors">{b.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{b.excerpt}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-gobra-terracotta">
                {t('read_more')} <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export const FaqSection = () => {
  const { t } = useApp();
  const flat = useTranslated(faqs.flatMap((f) => [f.q, f.a]));
  const items = faqs.map((f, i) => ({ q: flat[i * 2] || f.q, a: flat[i * 2 + 1] || f.a }));
  return (
    <section className="bg-gobra-sand/60 py-16 md:py-24">
      <div className="max-w-[900px] mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="h-1 w-12 bg-gobra-terracotta rounded-full mb-3 mx-auto" />
          <h2 className="font-title text-2xl md:text-4xl font-bold text-gobra-slate">{t('faqs')}</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {items.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="bg-white rounded-xl border border-gobra-sand px-5">
              <AccordionTrigger className="font-title text-left text-base md:text-lg font-semibold text-gobra-slate hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export const InstagramCta = () => {
  const { t, settings } = useApp();
  const companyInfo = settings;
  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-14">
      <div className="gobra-gradient rounded-2xl px-6 md:px-12 py-10 md:py-14 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
        <div>
          <div className="inline-flex items-center gap-2 text-gobra-orange font-semibold text-sm mb-2">
            <Instagram className="h-4 w-4" /> {companyInfo.instagramHandle}
          </div>
          <h2 className="font-title text-2xl md:text-3xl font-bold text-white">{t('instagram_title')}</h2>
          <p className="text-white/70 mt-2 max-w-xl">{t('instagram_desc')}</p>
        </div>
        <a href={companyInfo.instagram} target="_blank" rel="noopener noreferrer">
          <Button className="bg-white text-gobra-green hover:bg-gobra-orange hover:text-white rounded-full px-8 py-6 text-base">
            <Instagram className="h-5 w-5 mr-2" /> {t('follow_us')}
          </Button>
        </a>
      </div>
    </section>
  );
};
